import * as vp from 'vopay';
import { Box, Select, DateRangePicker, AVAILABLE_TIMEZONES } from 'vopay';
import { FunctionComponent, useState } from 'react';
import styles from './FileUploadHistory.module.scss';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { FileConversionFilterParams, FileConversionSearchParams } from './types';

type SearchFormProps = {
  conversionStatus: FileConversionSearchParams['conversionStatus'];
  paymentRail: FileConversionSearchParams['paymentRail'];
  service: FileConversionSearchParams['service'];
  timeZone: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  onSearch: (searchParams: FileConversionFilterParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const FileConversionTableFilters: FC = ({
  service,
  paymentRail,
  conversionStatus,
  timeZone,
  initialTimeRange,
  onSearch: search,
}: SearchFormProps) => {
  const [currentService, setCurrentService] = useState<string>(service);
  const [currentPaymentRail, setCurrentPaymentRail] = useState<string>(paymentRail);
  const [currentConversionStatus, setCurrentConversionStatus] = useState<string>(conversionStatus);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>(timeZone);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History.FileConversionHistoryForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));
  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  const transactionTypeOptions = [
    { value: 'all', label: tGlobal('PaymentRail.all') },
    { value: 'eft', label: tGlobal('PaymentRail.eft') },
    { value: 'ach', label: tGlobal('PaymentRail.ach') },
    { value: 'vopayinstant', label: tGlobal('PaymentRail.vopayinstant') },
    { value: 'rtp', label: tGlobal('PaymentRail.rtp') },
    { value: 'fedwire', label: tGlobal('PaymentRail.wire') },
  ];

  const paymentTypeOptions = [
    { value: 'all', label: tGlobal('ServiceOptions.all') },
    { value: 'Credit', label: tGlobal('ServiceOptions.credit') },
    { value: 'Debit', label: tGlobal('ServiceOptions.debit') },
  ];

  const statusOptions = [
    { value: 'all', label: t('Status.all', { defaultValue: 'All' }) },
    { value: 'Completed', label: t('Status.completed', { defaultValue: 'Completed' }) },
    { value: 'Failed', label: t('Status.failed', { defaultValue: 'Failed' }) },
    { value: 'Processing', label: t('Status.processing', { defaultValue: 'Processing' }) },
  ];

  return (
    <Box flx="row" gap="sm" wrap="wrap">
      <div className={styles.datePickerContainer}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
        />
      </div>

      <Box width="200px">
        <Select
          version="2"
          placeholder={t('transactionTypePlaceholder', { defaultValue: 'Transaction Type' })}
          options={transactionTypeOptions}
          name="transactionType"
          value={currentPaymentRail}
          sort={false}
          onChange={(e) => {
            setCurrentPaymentRail(e.target.value);
          }}
        />
      </Box>
      <Box width="200px">
        <Select
          version="2"
          placeholder={t('paymentTypePlaceholder', { defaultValue: 'Payment Type' })}
          options={paymentTypeOptions}
          name="paymentType"
          value={currentService}
          sort={false}
          onChange={(e) => {
            setCurrentService(e.target.value);
          }}
        />
      </Box>
      <Box width="200px">
        <Select
          version="2"
          placeholder={t('statusPlaceholder', { defaultValue: 'Status' })}
          options={statusOptions}
          name="conversionStatus"
          sort={false}
          value={currentConversionStatus}
          onChange={(e) => {
            setCurrentConversionStatus(e.target.value);
          }}
        />
      </Box>
      <Box width="260px">
        <Select
          version="2"
          placeholder={t('timeZonePlaceholder', { defaultValue: 'Time Zone' })}
          options={timeZoneOptions}
          name="timeZone"
          value={currentTimeZone}
          onChange={(e) => {
            setCurrentTimeZone(e.target.value as vp.Timezone);
          }}
        />
      </Box>
      <Button
        id="search-file-conversion-history"
        size="small"
        colorScheme="primary"
        onClick={() => {
          search({
            service: currentService as FileConversionSearchParams['service'],
            paymentRail: currentPaymentRail as FileConversionSearchParams['paymentRail'],
            conversionStatus: currentConversionStatus as FileConversionSearchParams['conversionStatus'],
            timeZone: currentTimeZone as vp.Timezone,
            timeRange,
          });
        }}
      >
        {tGlobal('Buttons.search')}
      </Button>
    </Box>
  );
};

export default FileConversionTableFilters;
